// ============================================================================
// AI INTELLIGENCE — MAIN COMPONENT
// Opportunity Hub: AI + Automation + Optimisation + delivery blueprint
// Uses AI to select the right intervention type from operational evidence
//
// Single AI call → single JSON → three rendered sections displayed as
// collapsible/scrollable panels within one tab.
//
// Column: ai_intelligence
// ============================================================================

import React, { useState, useEffect, useRef } from 'react';
import { getAIModelConfig, getInvokeAIProvider } from '@advisor-core/lib/aiModelConfig';
import { invokeWebAI } from '@advisor-core/lib/webAiClient';
import { BrainCircuit } from 'lucide-react';
import ExportModal from '@process-advisor/process-miner/consultant/ExportModal';
import { themeAiHtml, appDefaultReportTheme } from '@process-advisor/process-miner/consultant/reportThemeUtils';
import { buildAIIntelligenceExportHTML } from '@process-advisor/process-miner/consultant/aiIntelligence-renderer';
import {
  getModelMetrics, getModelMetricsTotal, getAppVersion, getAiConsultantContent,
  getIssueTypes, getProcessModelTitle, taskJSON,
  getModelID, getFieldUsage, getStatusTypeSummary, getStatusTypeMappings, getAttributePrivacyRules, getActivityType, getActivityContext, setActiveAction,
} from '@process-advisor/process-miner/processMiner/processMiner';

import { openProgressModal }   from '@process-advisor/process-miner/api/methods/modals/openProgressModal';
import { closeProgressModal }  from '@process-advisor/process-miner/api/methods/modals/closeProgressModal';
import { updateProgressModal } from '@process-advisor/process-miner/api/methods/modals/updateProgressModal';
import { startAiProgressTimer, stopAiProgressTimer, stopAllAiProgressTimers } from '@process-advisor/process-miner/consultant/aiProgressModalTimer';
import { apiProvider }         from '@process-advisor/process-miner/api/apiProvider';

import { buildAIIntelligencePrompt }      from '@process-advisor/process-miner/consultant/aiIntelligence-prompt';
import { renderAIIntelligenceHTML }       from '@process-advisor/process-miner/consultant/aiIntelligence-renderer';

import { replaceQuotes, sleep } from '@process-advisor/process-miner/dashboard/aiinsights/aiInsights-utils';
import { saveConsultantContent } from '@process-advisor/process-miner/consultant/saveConsultantContent';
import {
  getCachedConsultantContent, setCachedConsultantContent,
  isHtmlCurrentVersion,
  getCachedConsultantJson, setCachedConsultantJson,
} from '@process-advisor/process-miner/consultant/consultantContentState';
import HtmlPreviewFrame from '@process-advisor/process-miner/consultant/HtmlPreviewFrame';
import ConsultantQaPanel, { AskAiButton } from '@process-advisor/process-miner/consultant/ConsultantQaPanel';
import ConsultantTabHeader, { ReadyBadge, HEADER_META_TEXT_STYLE, getHeaderPrimaryButtonStyle, getHeaderSecondaryButtonStyle } from '@process-advisor/process-miner/consultant/ConsultantTabHeader';
import { SaveSnapshotButton, SaveSnapshotModal } from '@process-advisor/process-miner/consultant/ConsultantSnapshotManager';
import { saveSnapshot } from '@process-advisor/process-miner/consultant/consultantSnapshotService';
import { injectOrganizationContext } from '@process-advisor/process-miner/consultant/organizationProfile';
import useAiReportScopeControls from '@process-advisor/process-miner/consultant/useAiReportScopeControls';
import { invokeProcessOpenAIWithBilling } from '@process-advisor/process-miner/consultant/aiBillingAccess';

// ── Forge lazy loader ──────────────────────────────────────────────────────────
let forgeModule = null;
const loadForgeModule = async () => {
  if (!forgeModule) forgeModule = await import('@forge/bridge');
  return forgeModule;
};

const toInvokeProvider = (p) =>
  p === 'gemini' ? 'Gemini' : p === 'claude' ? 'Claude' : 'ChatGPT';

const SLEEP_MS  = 50;
const POLL_MS   = 1500;
const COLUMN    = 'ai_intelligence';

let processingCtr = 0;

const cleanAndParseJSON = (raw) => {
  let s = (raw || '{}')
    .replace(/^```json\s*/i, '').replace(/^```\s*/i, '').replace(/```\s*$/i, '').trim();
  const lb = s.lastIndexOf('}');
  if (lb !== -1) s = s.substring(0, lb + 1);
  try { return JSON.parse(s); } catch (_) {}
  const opens = [];
  for (const ch of s) {
    if (ch === '{' || ch === '[') opens.push(ch);
    else if (ch === '}' || ch === ']') opens.pop();
  }
  let r = s.replace(/,?\s*"[^"]*"?\s*:?\s*[^,\}\]]*$/, '');
  for (let i = opens.length - 1; i >= 0; i--) r += opens[i] === '{' ? '}' : ']';
  return JSON.parse(r);
};


const decodeHtmlEntities = (value = '') => String(value)
  .replace(/&quot;/g, '"')
  .replace(/&#34;/g, '"')
  .replace(/&#39;/g, "'")
  .replace(/&lt;/g, '<')
  .replace(/&gt;/g, '>')
  .replace(/&amp;/g, '&');

const extractEmbeddedJson = (html) => {
  if (!html) return null;
  try {
    const match = String(html).match(/<script id=["']__qa_data__["'][^>]*>([\s\S]*?)<\/script>/);
    if (!match) return null;
    return JSON.parse(decodeHtmlEntities(match[1]).trim());
  } catch (_) {
    return null;
  }
};


// ── Main component ─────────────────────────────────────────────────────────────

const AIIntelligence = ({ processLabel = 'Process', entityLabel = 'Work Item', refreshKey, aiModelConfig }) => {

  const modelId = getModelID();

  const [isGenerating, setIsGenerating]     = useState(false);
  const [generated, setGenerated]           = useState(false);
  const [savedHtml, setSavedHtml]           = useState(null);
  const [assessment, setAssessment]         = useState(null);
  const [generationTime, setGenerationTime] = useState(null);
  const [requestId, setRequestId]           = useState(null);
  const [status, setStatus]                 = useState(null);
  const [isQaPanelOpen, setIsQaPanelOpen]   = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [snapshotModalOpen, setSnapshotModalOpen] = useState(false);
  const [snapshotSaving, setSnapshotSaving] = useState(false);
  const [qaOpen, setQaOpen] = useState(false);
  const pollRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const aiProgressTimerRef = useRef(null);

  const aiScope = useAiReportScopeControls({
    modelId,
    reportKey: 'aiIntelligence',
    reportLabel: 'AI, Automation & Optimisation',
    getTaskRows: taskJSON,
    getVariants: () => apiProvider.variantsTable() || [],
    getProcessSteps: () => apiProvider.processStepsTable() || [],
    getPrivacyRules: getAttributePrivacyRules, getActivityType, getActivityContext,
  });

  // Load cached on mount — session cache first, then DB
  useEffect(() => {
    const cached = getCachedConsultantContent({ modelId, column: COLUMN });
    const dbHtml = getAiConsultantContent ? getAiConsultantContent(COLUMN) : null;
    const saved = cached || (isHtmlCurrentVersion(dbHtml) ? dbHtml : null);
    if (saved) {
      setSavedHtml(saved);
      setGenerated(true);
      if (!cached) setCachedConsultantContent({ modelId, column: COLUMN, html: saved });

      const cachedJson = getCachedConsultantJson({ modelId, column: COLUMN });
      const embeddedJson = cachedJson || extractEmbeddedJson(saved);
      if (embeddedJson) {
        setAssessment(embeddedJson);
        if (!cachedJson) setCachedConsultantJson({ modelId, column: COLUMN, json: embeddedJson });
      }
    }
  }, [modelId]);

  // Forge poll loop — mirrors executiveAssessment.jsx exactly
  useEffect(() => {
    if (!requestId || !(status?.status === 'pending' || status?.status === 'processing')) return;

    pollRef.current = setInterval(async () => {
      try {
        const { invoke } = await loadForgeModule();
        const sd = await invoke('checkStatus', { requestId });
        setStatus(sd);
        processingCtr += 1;
        updateProgressModal({ status: 'Opportunity Hub — ' + processingCtr + 's', ai: true });

        if (sd?.status === 'completed') {
          clearInterval(pollRef.current);
          const raw  = sd?.result || '{}';
          const json = cleanAndParseJSON(typeof raw === 'string' ? raw : JSON.stringify(raw));
          replaceQuotes(json);
          setGenerationTime(processingCtr.toString());
          const html = themeAiHtml(renderAIIntelligenceHTML(json, getProcessModelTitle()), appDefaultReportTheme());
          setSavedHtml(html); setAssessment(json); setGenerated(true);
          setCachedConsultantContent({ modelId, column: COLUMN, html });
          setCachedConsultantJson({ modelId, column: COLUMN, json });
          saveConsultantContent({ modelId, column: COLUMN, html, AppVersion: getAppVersion() });
          setIsGenerating(false); stopAiProgressTimer(aiProgressTimerRef.current); aiProgressTimerRef.current = null; if (pollIntervalRef.current) { clearInterval(pollIntervalRef.current); pollIntervalRef.current = null; } closeProgressModal();
        }
        if (sd?.status === 'failed') {
          clearInterval(pollRef.current);
          setIsGenerating(false); stopAiProgressTimer(aiProgressTimerRef.current); aiProgressTimerRef.current = null; if (pollIntervalRef.current) { clearInterval(pollIntervalRef.current); pollIntervalRef.current = null; } closeProgressModal();
        }
      } catch (err) {
        console.error('[AIIntelligence] Poll error:', err);
        clearInterval(pollRef.current);
        setIsGenerating(false); stopAiProgressTimer(aiProgressTimerRef.current); aiProgressTimerRef.current = null; if (pollIntervalRef.current) { clearInterval(pollIntervalRef.current); pollIntervalRef.current = null; } closeProgressModal();
      }
    }, 1000);

    return () => pollRef.current && clearInterval(pollRef.current);
  }, [requestId, status?.status]);

  // ── Data preparation ────────────────────────────────────────────────────────

  const prepareTransitions = () => {
    const src = aiScope.getScopedProcessSteps() || [];
    return src.slice(0, 40).map((t) => ({
      Transition:            t.step,
      'From Activity':       t.from_activity,
      'To Activity':         t.to_activity,
      'Work items':          t.tasks,
      'Avg deviation (hrs)': t.avg_duration_hrs?.toFixed(2) ?? 'N/A',
      'Std deviation (hrs)': t.std_deviation_hrs?.toFixed(2) ?? 'N/A',
      [`% total ${apiProvider.getEntityUpper()}s`]:
        t.perc_total_tasks != null ? (t.perc_total_tasks * 100).toFixed(2) : 'N/A',
      'Is rework?': t.loop,
    }));
  };

  const prepareVariants = () => {
    const src = aiScope.getScopedVariants() || [];
    return src.slice(0, 30).map((v) => ({
      Variant:  v.variant,
      ID:       v.variant_id,
      [`${apiProvider.getEntityUpper()}s`]: v.tasks,
      [`% Total ${apiProvider.getEntityUpper()}s`]:
        v.perc_total_tasks != null ? (Number(v.perc_total_tasks) * 100).toFixed(2) : 'N/A',
      'Is Rework':           v.loop,
      Loops:                 v.loops,
      'Std Deviation (hrs)': v.std_deviation_hrs != null ? Number(v.std_deviation_hrs).toFixed(2) : 'N/A',
      'Avg Duration (hrs)':  v.avg_duration_hrs  != null ? Number(v.avg_duration_hrs).toFixed(2)  : 'N/A',
    }));
  };

  const prepareMetrics = () => {
    const metrics = getModelMetrics();
    return metrics.filter(m => m.work_items > 0).map(m => ({
      [`${apiProvider.getEntityUpper()}s`]: m.work_items,
      Variants:          m.variants,
      Activities:        m.activities,
      Steps:             m.steps,
      'Avg Transitions': m.avg_transitions.toFixed(1),
      'Avg Duration':    (m.cycle_time    / 3600).toFixed(2),
      'Std Deviation':   (m.std_deviation / 3600).toFixed(2),
      '% Rework':        (m.rework_rate   * 100 ).toFixed(2),
    }));
  };

  const handlePreviewMessage = (message) => {
    if (message?.type !== 'metricus-opportunity-action' || !message?.opportunity) return;
    const opportunity = message.opportunity;
    if (message.action === 'value') {
      try {
        window.localStorage.setItem(`metricus.pendingValueOpportunity.${modelId || 'unsaved'}`, JSON.stringify({
          opportunity,
          createdAt: new Date().toISOString(),
        }));
      } catch (_) {}
      if (typeof setActiveAction === 'function') setActiveAction('valueSimulation');
      try {
        window.dispatchEvent(new CustomEvent('metricus:navigate-mode', { detail: { mode: 'valueSimulation' } }));
      } catch (_) {}
      return;
    }
    if (message.action === 'execution') {
      try {
        window.localStorage.setItem(`metricus.executionOpportunityContext.${modelId || 'unsaved'}`, JSON.stringify({
          opportunity,
          createdAt: new Date().toISOString(),
        }));
      } catch (_) {}
      try {
        window.dispatchEvent(new CustomEvent('metricus:navigate-mode', { detail: { mode: 'aiExecutionStrategy' } }));
        window.dispatchEvent(new CustomEvent('metricus:consultant-tab-change', { detail: { tabId: 'aiExecutionStrategy' } }));
      } catch (_) {}
    }
  };

  // ── Export handlers ────────────────────────────────────────────────────────

  const handleExportHTML = (themeId = appDefaultReportTheme()) => {
    const contentJson = assessment || getCachedConsultantJson({ modelId, column: COLUMN }) || extractEmbeddedJson(savedHtml);
    const html = contentJson ? themeAiHtml(buildAIIntelligenceExportHTML(contentJson, getProcessModelTitle()), themeId) : savedHtml;
    if (!html) return;
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url;
    a.download = `ai-opportunities-${(getProcessModelTitle() || 'report').replace(/\s+/g, '-').toLowerCase()}.html`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    setIsExportModalOpen(false);
  };

  const handleExportPDF = (themeId = appDefaultReportTheme()) => {
    const contentJson = assessment || getCachedConsultantJson({ modelId, column: COLUMN }) || extractEmbeddedJson(savedHtml);
    const html = contentJson ? themeAiHtml(buildAIIntelligenceExportHTML(contentJson, getProcessModelTitle()), themeId) : savedHtml;
    if (!html) return;
    const win = window.open('', '_blank', 'width=1400,height=900');
    if (!win) return;
    win.document.open(); win.document.write(html); win.document.close();
    win.focus(); setTimeout(() => win.print(), 300);
    setIsExportModalOpen(false);
  };

  const handleExportJSON = () => {
    const contentJson = assessment || getCachedConsultantJson({ modelId, column: COLUMN }) || extractEmbeddedJson(savedHtml);
    if (!contentJson) return;
    const blob = new Blob([JSON.stringify(contentJson, null, 2)], { type: 'application/json;charset=utf-8' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url;
    a.download = `ai-opportunities-${(getProcessModelTitle() || 'report').replace(/\s+/g, '-').toLowerCase()}.json`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    setIsExportModalOpen(false);
  };

  const handleExportPPTX = async (themeId = appDefaultReportTheme()) => {
    const contentJson = assessment || getCachedConsultantJson({ modelId, column: COLUMN }) || extractEmbeddedJson(savedHtml);
    if (!contentJson) {
      console.warn('[AIIntelligence] PPTX export skipped: no JSON data available for the Opportunity Hub. Regenerate the content to enable PPTX export.');
      return;
    }
    try {
      // Lazy import keeps pptxgenjs out of the main bundle until first use.
      const { buildAiContentPptx } = await import('@process-advisor/process-miner/consultant/aiContent-pptx');
      const pptx = buildAiContentPptx('aiIntelligence', contentJson, getProcessModelTitle(), { theme: themeId });
      await pptx.writeFile({ fileName: `ai-opportunities-${(getProcessModelTitle() || 'report').replace(/\s+/g, '-').toLowerCase()}.pptx` });
    } catch (error) {
      console.error('PPTX export failed', error);
    }
    setIsExportModalOpen(false);
  };

  // ── Generate ────────────────────────────────────────────────────────────────

  const handleSaveSnapshot = async (description) => {
    setSnapshotSaving(true);
    try {
      const html = savedHtml || null;
      if (!html) return;
      const result = await saveSnapshot({
        modelId, tab: 'aiIntelligence', tabLabel: 'AI and Automation',
        description, html, AppVersion: getAppVersion(),
      });
      if (!result?.success) throw new Error('Snapshot was not saved');
      setSnapshotModalOpen(false);
    } catch (error) {
      console.error('Advisor snapshot save failed', error);
      window.alert(`Unable to save Advisor Snapshot: ${error.message || error}`);
    } finally {
      setSnapshotSaving(false);
    }
  };

  const generate = async () => {
    if (isGenerating) return;
    const startTime = Date.now();
    processingCtr = 0;
    stopAllAiProgressTimers();
    setIsGenerating(true); setSavedHtml(null); setGenerated(false); setGenerationTime(null);

    const AppVersion  = getAppVersion();
    const issueTypes   = getIssueTypes();
    const issueTypeStr = issueTypes.length === 1
      ? `Work Type = ${issueTypes[0]}`
      : `Work Types = ${issueTypes.join(' or ')}`;

    await openProgressModal({ status: 'Generating AI & Automation Opportunities…' });
    await sleep(SLEEP_MS);

    const args = {
      issueTypeStr,
      metricsHistory:     prepareMetrics(),
      metricsTotal:       typeof getModelMetricsTotal === 'function' ? getModelMetricsTotal() : null,
      transitionsJSON:    prepareTransitions(),
      variantsJSON:       prepareVariants(),
      sampleItems:        aiScope.getScopedTaskRows(),
      reportScopeSummary: aiScope.getReportScopeSummary(),
      fieldUsage:         getFieldUsage(),
      statusTypeSummary:  getStatusTypeSummary  ? getStatusTypeSummary()  : null,
      statusTypeMappings: getStatusTypeMappings ? getStatusTypeMappings() : [],
      activityType: typeof getActivityType === 'function' ? getActivityType() : null,
      activityContext: typeof getActivityContext === 'function' ? getActivityContext() : null,
      appVersion: AppVersion,
      sourceSystem: AppVersion === 'SNOW'
        ? 'servicenow'
        : AppVersion === 'FORGE'
          ? 'jira'
          : (typeof window!=='undefined' ? (window.__PA_WEB_AI_CONTEXT__?.model?.sourceSystem || 'generic') : 'generic'),
    };

    const prompt = await injectOrganizationContext(buildAIIntelligencePrompt(args));
    const { provider, model, temperature } = getAIModelConfig(aiModelConfig);

    // ── Forge path ──
    if (AppVersion === 'FORGE') {
      try {
        const { invoke } = await loadForgeModule();
        const response   = await invokeProcessOpenAIWithBilling({
          invoke,
          reportLabel: 'AI, Automation & Optimisation',
          payload: {
            prompt,
            aiProvider:  toInvokeProvider(provider),
            model,
            temperature,
          },
        });
        setRequestId(response.requestId);
        setStatus({ status: 'pending' });
      } catch (err) {
        console.error('[AIIntelligence] Forge error:', err);
        setIsGenerating(false); stopAiProgressTimer(aiProgressTimerRef.current); aiProgressTimerRef.current = null; if (pollIntervalRef.current) { clearInterval(pollIntervalRef.current); pollIntervalRef.current = null; } closeProgressModal();
      }
      return;
    }

    // ── Web SaaS path ──
    if (AppVersion === 'WEB') {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      aiProgressTimerRef.current = await startAiProgressTimer('Generating AI, Automation & Optimisation Opportunities');
      try {
        const result = await invokeWebAI({
          prompt,
          reportLabel: 'AI, Automation & Optimisation',
          temperature,
          responseFormat: 'json',
          modelId,
        });
        const json = cleanAndParseJSON(result?.text || '{}');
        replaceQuotes(json);
        const dur=((Date.now()-startTime)/1000).toFixed(1);
        setGenerationTime(dur);
        const html=themeAiHtml(renderAIIntelligenceHTML(json,getProcessModelTitle()),appDefaultReportTheme());
        setSavedHtml(html);setAssessment(json);setGenerated(true);
        setCachedConsultantContent({modelId,column:COLUMN,html});
        setCachedConsultantJson({modelId,column:COLUMN,json});
        saveConsultantContent({modelId,column:COLUMN,html,AppVersion});
      } catch (err) {
        console.error('[AIIntelligence] Web AI error:',err);
      } finally {
        setIsGenerating(false);
        stopAiProgressTimer(aiProgressTimerRef.current); aiProgressTimerRef.current=null;
        if (pollIntervalRef.current) { clearInterval(pollIntervalRef.current); pollIntervalRef.current=null; }
        closeProgressModal();
      }
      return;
    }

    // ── Direct API path ──
    if (AppVersion === 'SNOW') {
      const _geminiKey = (typeof API_KEY !== 'undefined' && API_KEY) ? API_KEY : undefined;
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      aiProgressTimerRef.current = await startAiProgressTimer('Generating AI, Automation & Optimisation Opportunities');
      try {
        const res  = await fetch(
          `https://generativelanguage.googleapis.com/v1beta/models/${model}:generateContent?key=${_geminiKey}`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              contents: [{ parts: [{ text: prompt + '\n\nYou must output valid JSON. Do not include ``` at beginning and end' }] }],
              generationConfig: { responseMimeType: 'application/json', maxOutputTokens: 32768 },
            }),
          }
        );
        const data = await res.json();
        const text = data?.candidates?.[0]?.content?.parts?.[0]?.text || '{}';
        const json = cleanAndParseJSON(text);
        replaceQuotes(json);
        const dur  = ((Date.now() - startTime) / 1000).toFixed(1);
        setGenerationTime(dur);
        const html = themeAiHtml(renderAIIntelligenceHTML(json, getProcessModelTitle()), appDefaultReportTheme());
        setSavedHtml(html); setAssessment(json); setGenerated(true);
        setCachedConsultantContent({ modelId, column: COLUMN, html });
        setCachedConsultantJson({ modelId, column: COLUMN, json });
        saveConsultantContent({ modelId, column: COLUMN, html, AppVersion });
      } catch (err) {
        console.error('[AIIntelligence] direct API error:', err);
      }
    }

    setIsGenerating(false); stopAiProgressTimer(aiProgressTimerRef.current); aiProgressTimerRef.current = null; if (pollIntervalRef.current) { clearInterval(pollIntervalRef.current); pollIntervalRef.current = null; } closeProgressModal();
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  const currentAppVersion = getAppVersion();
  const webSourceSystem = typeof window!=='undefined' ? String(window.__PA_WEB_AI_CONTEXT__?.model?.sourceSystem || '').trim() : '';
  const normalizedWebSource = webSourceSystem.toLowerCase();
  const isServiceNow = currentAppVersion === 'SNOW' || normalizedWebSource.includes('servicenow');
  const isJira = currentAppVersion === 'FORGE' || normalizedWebSource.includes('jira') || normalizedWebSource.includes('atlassian');
  const platformLabel = isServiceNow ? 'ServiceNow' : isJira ? 'Jira / Jira Service Management' : (webSourceSystem || 'Source platform');
  const aiCapabilityLabel = isServiceNow ? 'ServiceNow AI and automation capabilities' : isJira ? 'Atlassian Rovo capabilities' : 'AI and automation capabilities';
  const opportunityChipLabel = 'AI · Automation · Optimisation';
  const pregenerateDescription = isServiceNow
    ? 'Uses AI to analyse this process evidence and identify the best opportunities for process optimisation, deterministic automation, and ServiceNow AI — including the right intervention, evidence, priority, and delivery path.'
    : isJira
      ? 'Uses AI to analyse this process evidence and identify the best opportunities for process optimisation, Jira Automation, and Atlassian AI / Rovo — including the right intervention, evidence, priority, and delivery path.'
      : 'Uses AI to analyse this process evidence and identify the best opportunities for process optimisation, deterministic automation, and appropriate AI capabilities — without assuming a specific technology platform.';

  return (
    <div style={{ backgroundColor: '#f8f9fa', padding: 0, display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>

        {/* ── Header bar ── */}
        <ConsultantTabHeader
          title="AI, Automation & Optimisation Opportunities"
          subtitle={`Uses AI to identify what to optimise, what to automate, and where ${isServiceNow ? 'ServiceNow AI' : isJira ? 'Atlassian AI / Rovo' : 'AI'} can deliver the greatest value`}
          icon={<BrainCircuit size={20} color="white" />}
          iconBackground="linear-gradient(135deg, #0052CC 0%, #2684FF 100%)"
          titleColor="#0052CC"
          actions={(
            <>
              {!generated && <ReadyBadge color="#0C66E4" />}
              {aiScope.controls}
              {generated && (
                <>
                  <AskAiButton hasContent={generated} onClick={() => setQaOpen(true)} />
                  <button
                    style={getHeaderPrimaryButtonStyle('#0052CC')}
                    onClick={() => { setSavedHtml(null); setGenerated(false); setGenerationTime(null); }}
                  >
                    New Analysis
                  </button>
                  {(typeof window === "undefined" || window.__METRICUS_JIRA_PRODUCT_EDITION__ !== "free") && <SaveSnapshotButton color="#0052CC" onClick={() => setSnapshotModalOpen(true)} />}
                  <button
                    style={getHeaderSecondaryButtonStyle('#1868DB')}
                    onClick={() => setIsExportModalOpen(true)}
                  >
                    Export...
                  </button>
                </>
              )}
            </>
          )}
        />

        {aiScope.panels}

        {/* ── Pre-generate panel ── */}
        {!generated && (
          <div style={{ background: '#fff', padding: '28px 32px 32px', borderRadius: '0 0 8px 8px', margin: '0 10px 10px', border: '2px solid #dfe1e6', borderTop: 'none', textAlign: 'center' }}>
            <div style={{
              width: 56, height: 56, margin: '0 auto 16px',
              background: 'linear-gradient(135deg, #1868DB 0%, #4C8FEB 100%)',
              borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <BrainCircuit size={28} color="white" />
            </div>
            <h3 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 700, color: '#172b4d' }}>
              Generate Opportunity Portfolio
            </h3>
            <p style={{ margin: '0 0 24px', fontSize: 14, color: '#5e6c84', lineHeight: 1.6, maxWidth: 520, marginLeft: 'auto', marginRight: 'auto' }}>
{pregenerateDescription}
            </p>

            {/* Section preview chips */}
            <div style={{ display: 'flex', justifyContent: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 24 }}>
              {[
                { label: opportunityChipLabel, color: '#0052CC', bg: '#E6F1FB' },
                { label: 'Implementation Sequence', color: '#FF8B00', bg: '#FFF0B3' },
                { label: 'Target Process State', color: '#00875A', bg: '#E3FCEF' },
              ].map((chip) => (
                <span key={chip.label} style={{
                  padding: '6px 14px', borderRadius: 999, fontSize: 12, fontWeight: 700,
                  background: chip.bg, color: chip.color,
                  border: `1px solid ${chip.color}40`,
                }}>
                  {chip.label}
                </span>
              ))}
            </div>

            <button
              onClick={generate}
              disabled={isGenerating}
              style={{
                background: isGenerating ? '#dfe1e6' : 'linear-gradient(135deg, #1868DB 0%, #4C8FEB 100%)',
                color: isGenerating ? '#97a0af' : '#fff',
                border: 'none', padding: '10px 28px', borderRadius: 6,
                fontSize: 15, fontWeight: 600,
                cursor: isGenerating ? 'not-allowed' : 'pointer',
                display: 'inline-flex', alignItems: 'center', gap: 8,
              }}
            >
              <BrainCircuit size={18} />
              {isGenerating ? 'Generating…' : 'Generate Opportunity Portfolio'}
            </button>
          </div>
        )}

        {/* ── Rendered output ── */}
        {generated && savedHtml && (
          <HtmlPreviewFrame html={savedHtml} title="AI, Automation & Optimisation Opportunities" onMessage={handlePreviewMessage} />
        )}

      </div>

      <ConsultantQaPanel
        isOpen={qaOpen}
        onClose={() => setQaOpen(false)}
        tabId="ai-intelligence"
        tabLabel="AI, Automation & Optimisation"
        themeColor="#1868DB"
        resultData={assessment}
        savedHtml={savedHtml}
        generated={generated}
        modelTitle={getProcessModelTitle()}
        processLabel={processLabel}
        entityLabel={entityLabel}
        aiModelConfig={aiModelConfig}
      />

      <SaveSnapshotModal
        isOpen={snapshotModalOpen}
        onClose={() => setSnapshotModalOpen(false)}
        onSave={handleSaveSnapshot}
        tab="aiIntelligence"
        tabLabel="AI, Automation & Optimisation"
        saving={snapshotSaving}
      />
      <ExportModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        title="Export AI, Automation & Optimisation Opportunities"
        onExportHTML={handleExportHTML}
        onExportPDF={handleExportPDF}
        onExportJSON={handleExportJSON} onExportPPTX={handleExportPPTX}
      />
    </div>
  );
};

export default AIIntelligence;
